import { Download, Image } from 'lucide-react';
import { useQrGenerator } from '../../hooks/useQrGenerator';
import { downloadStyledPng, downloadStyledSvg } from '../../utils/downloadStyledQr';
import ContentTypeTabs from './ContentTypeTabs';
import ContentInputs from './ContentInputs';
import DesignPanel from './DesignPanel';
import GeneratorStep from './GeneratorStep';
import QrPreview from './QrPreview';
import BuyMeACoffeeButton from './BuyMeACoffeeButton';

const TRUST_BADGES = [
  'Free Forever',
  'No Tracking',
  'Instant Download',
  'Browser-Based',
];

export default function QrGenerator() {
  const generator = useQrGenerator();
  const { qrValue, style, hasContent } = generator;

  const handleDownloadPng = () => downloadStyledPng(qrValue, style);
  const handleDownloadSvg = () => downloadStyledSvg(qrValue, style);

  return (
    <section id="generator" className="relative pb-16">
      <div className="absolute inset-0 -z-10 bg-gradient-to-b from-indigo-50/60 via-slate-50 to-slate-50" />

      <div className="mx-auto max-w-6xl px-4 pt-8 sm:px-6 lg:px-8 lg:pt-10">
        {/* Compact hero */}
        <header className="mx-auto max-w-2xl text-center">
          <h1 className="text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
            Free QR Code Generator
          </h1>
          <p className="mt-3 text-base text-slate-600 sm:text-lg">
            No redirects. No sign-up. Download in seconds.
          </p>
          <ul className="mt-4 flex flex-wrap justify-center gap-2">
            {TRUST_BADGES.map((badge) => (
              <li
                key={badge}
                className="rounded-full bg-white/90 px-3 py-1 text-xs font-medium text-slate-600 ring-1 ring-slate-200/80"
              >
                {badge}
              </li>
            ))}
          </ul>
        </header>

        {/* Unified workspace */}
        <div className="mt-8 overflow-hidden rounded-2xl bg-white shadow-xl shadow-slate-200/40 ring-1 ring-slate-200/80 lg:mt-10">
          <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_300px] xl:grid-cols-[minmax(0,1fr)_320px]">
            {/* Controls */}
            <div className="divide-y divide-slate-100 p-6 sm:p-8">
              <GeneratorStep
                step="1"
                title="Content"
                description="Choose a type and enter what your QR code should open."
              >
                <ContentTypeTabs
                  active={generator.contentType}
                  onChange={generator.setContentType}
                />
                <div className="mt-5">
                  <ContentInputs
                    contentType={generator.contentType}
                    fields={generator.fields}
                    updateField={generator.updateField}
                  />
                </div>
              </GeneratorStep>

              <GeneratorStep
                step="2"
                title="Design"
                description="Shape, frame, logo, and colors."
              >
                <DesignPanel
                  embedded
                  style={style}
                  updateStyle={generator.updateStyle}
                />
              </GeneratorStep>

              <GeneratorStep step="3" title="Download" className="!pb-0">
                <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                  <button
                    type="button"
                    disabled={!hasContent}
                    onClick={handleDownloadPng}
                    className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-indigo-600 px-5 text-sm font-semibold text-white shadow-md shadow-indigo-600/25 transition hover:bg-indigo-700 disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[160px] sm:flex-none"
                  >
                    <Download className="h-4 w-4" />
                    Download PNG
                  </button>
                  <button
                    type="button"
                    disabled={!hasContent}
                    onClick={handleDownloadSvg}
                    className="inline-flex h-11 flex-1 items-center justify-center gap-2 rounded-xl bg-slate-50 px-5 text-sm font-semibold text-slate-700 ring-1 ring-slate-200 transition hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-50 sm:min-w-[160px] sm:flex-none"
                    title={
                      style.frameType !== 'none'
                        ? 'SVG export is available without a frame; with a frame, PNG is used.'
                        : undefined
                    }
                  >
                    <Image className="h-4 w-4" />
                    Download SVG
                  </button>
                  <BuyMeACoffeeButton />
                </div>
                <p className="mt-4 text-center text-xs text-slate-400 sm:text-left">
                  {style.frameType !== 'none'
                    ? 'Frame is included in PNG export.'
                    : 'High-resolution PNG and SVG, generated in your browser.'}
                </p>
              </GeneratorStep>
            </div>

            {/* Sticky preview */}
            <aside className="border-t border-slate-100 bg-gradient-to-b from-slate-50/80 to-white p-6 lg:sticky lg:top-20 lg:self-start lg:border-l lg:border-t-0 lg:p-8">
              <QrPreview value={qrValue} style={style} />
            </aside>
          </div>
        </div>
      </div>
    </section>
  );
}
